import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { addTowishlistApi } from "../../../ApiService/addtowishlist/addtowishlist";
import { getProductsBybrand } from "../../../ApiService/Brand/productbrand";
import { fetchfilterproduct } from "../../../ApiService/filterapi/filterapi.js";
import { getProductsByCategory } from "../../../ApiService/productcategory/productcategory.js";
import { getProductsdetail } from "../../../ApiService/productdetail/productdetail";
import { getProductsBysubCategory } from "../../../ApiService/productsubcatagory/productsubcategory";
import { fetchshuffledproducts } from "../../../ApiService/Shuffled-products/Shuffled-products.js";
import { getCurrencySymbol } from "../../../ApiService/stockapi/currency.js";
import { FiEdit, FiEye, FiFilter, FiHeart } from "../../../assets/icons/vander";
import fallbackImage from "../../../assets/images/categories/football.jpg";
import BottomNav from "../../../components/bottomnav.jsx";
import { CategoryContext } from "../../../components/CategoryContext";
import Filter from "../../../components/filter";
import Footer from "../../../components/footer";
import Navbar from "../../../components/navbar";
import ScrollToTop from "../../../components/scroll-to-top";
import Switcher from "../../../components/switcher";

const BASE_MEDIA_URL = process.env.REACT_APP_MEDIA_BASE_URL;

export default function ShopGridLeftSidebar() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { categories, subcategories, brand } = useContext(CategoryContext);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 8; // show 8 products per page
  const queryParams = new URLSearchParams(location.search);
  // const initialSearchTerm = queryParams.get("search") || "";

  let category = null;
  let subcategory = null;
  let brands = null;

  if (location.pathname.includes("/category/")) {
    category = categories.find(
      (cat) => String(cat.category_name_id) === String(id)
    );
  } else if (location.pathname.includes("/subcategory/")) {
    subcategory = subcategories.find(
      (sub) => String(sub.sub_category_details?.sub_category_id) === String(id)
    );
  } else if (location.pathname.includes("/brand/")) {
    brands = brand.find(
      (b) => String(b.brand_details?.brand_id) === String(id)
    );
  }

  // 🔍 Fetch products dynamically when user types in search bar
  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchTerm.trim() !== "") {
        try {
          setLoading(true);
          const data = await fetchfilterproduct({}, searchTerm.trim());
          console.log("Search API results:", data);
          setFilteredProducts(data.results || data || []);
        } catch (error) {
          console.error("Search failed:", error);
          setFilteredProducts([]);
        } finally {
          setLoading(false);
        }
      } else {
        // Reset when search box is cleared
        setFilteredProducts([]);
      }
    }, 500); // debounce 500ms

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Fetch default products based on URL
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        let data = [];
        if (location.pathname.includes("/subcategory/")) {
          console.log(`Fetching products for subcategory ID: ${id}`);
          data = await getProductsBysubCategory(id);
        } else if (location.pathname.includes("/category/")) {
          console.log(`Fetching products for category ID: ${id}`);
          data = await getProductsByCategory(id);
        } else if (location.pathname.includes("/brand/")) {
          console.log(`Fetching products for brand ID: ${id}`);
          data = await getProductsBybrand(id);
        } else if (location.pathname.includes("/allproducts")) {
          console.log("Fetching All products...");
          data = await fetchshuffledproducts();
        } else {
          console.log(
            "No category, subcategory, or brand in URL — fetching all products"
          );
          setError("No category or subcategory selected");
        }

        console.log("Default products:", data);
        setProducts(data.results || []);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [id, location.pathname]);

  // Fetch filtered products when filters change
  useEffect(() => {
    const getFilteredProducts = async () => {
      try {
        const filters = {
          categories: selectedCategories.map((c) => c.category_id),
          brands: selectedBrands.map((b) => b.brand_id),
          colors: selectedColors.map((c) => c.code),
          sizes: selectedSizes.map((s) => s.code),
        };

        console.log("Applying filters:", filters);

        // Only call API if any filter is selected
        const hasFilters = Object.values(filters).some((arr) => arr.length > 0);
        if (hasFilters) {
          setLoading(true);
          const data = await fetchfilterproduct(filters);
          console.log("Filtered products response:", data);
          setFilteredProducts(data.results || []);
        } else {
          // Clear filtered products when no filters are applied
          setFilteredProducts([]);
        }
      } catch (error) {
        console.error("Failed to fetch filtered products:", error);
        setFilteredProducts([]);
        setError("Failed to fetch filtered products");
      } finally {
        setLoading(false);
      }
    };

    getFilteredProducts();
  }, [selectedCategories, selectedBrands, selectedColors, selectedSizes]);

  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedBrands.length > 0 ||
    selectedColors.length > 0 ||
    selectedSizes.length > 0;

  const baseProducts = hasActiveFilters ? filteredProducts : products;

  // Apply search filter
  const displayedProducts = baseProducts.filter((p) =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedColors([]);
    setSelectedSizes([]);
    setFilteredProducts([]);
  };

  const handleProductClick = async (productId) => {
    try {
      const data = await getProductsdetail(productId);
      navigate(`/product-detail-three/products/${productId}`);
    } catch (err) {
      console.error("Failed to fetch product details:", err);
    }
  };

  const handleCustomizeClick = (product) => {
    navigate(`/customize/${product.product_id}`, {
      state: {
        product,
        hideShareSize: true,
        hideSelection: true,
      },
    });
  };

  const handleAddToWishlist = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const customer_id = localStorage.getItem("customer_id");
      if (!customer_id) {
        toast.error("Please login first!", {
          position: "top-center",
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
        return;
      }

      const firstVariant = product.variants?.[0];
      const firstSize = firstVariant?.sizes?.[0];
      const sku_code = firstSize?.sku || firstVariant?.sku_code || null;

      if (!sku_code) {
        toast.error("Product information incomplete!", {
          position: "top-center",
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
        return;
      }

      const payload = {
        product_id: product?.product_id || product?.id,
        sku_code,
        customer_id,
      };

      const response = await addTowishlistApi(payload);

      if (response?.message) {
        toast.success("Added to wishlist ❤️", {
          position: "top-center",
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
      } else {
        toast.error(response?.error || "Failed to add to wishlist!", {
          position: "top-center",
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
      }
    } catch (error) {
      console.error("Wishlist API error:", error);
      toast.error("Something went wrong!", {
        position: "top-center",
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
    }
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = displayedProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const totalPages = Math.ceil(displayedProducts.length / productsPerPage);

  // ✅ 4️⃣ Decide which product list to show
  const hasSearch = searchTerm.trim() !== "";
  const hasFilters =
    selectedCategories.length > 0 ||
    selectedBrands.length > 0 ||
    selectedColors.length > 0 ||
    selectedSizes.length > 0;

  const displayProducts = hasSearch
    ? filteredProducts
    : hasFilters
    ? filteredProducts
    : products;

  return (
    <>
      <Navbar navClass="defaultscroll is-sticky" />
      <section className="relative table w-full pt-12 lg:pt-12 md:pt-28 bg-gray-50 dark:bg-slate-800">
        <div className="container relative">
          <div className="grid grid-cols-1 mt-14">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-3xl leading-normal font-semibold">
                {subcategory
                  ? `Subcategory - ${subcategory.sub_category_details?.sub_category_name}`
                  : category
                  ? `Category - ${category.category_name}`
                  : brands
                  ? `Brand - ${brands?.brand_details?.brand_name}`
                  : location.pathname.includes("/allproducts")
                  ? "All Products"
                  : "Products"}
              </h3>

              {/* Mobile Filter Toggle Button */}
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="md:hidden mt-4 sm:mt-0 flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md"
              >
                <FiFilter className="text-lg" />
                {isFilterOpen ? "Hide Filters" : "Show Filters"}
              </button>
            </div>

            {selectedCategories.length > 0 ||
              selectedBrands.length > 0 ||
              selectedColors.length > 0 ||
              selectedSizes.length > 0}
          </div>
        </div>
      </section>

      <section className="relative md:py-2 py-10">
        <div className="container relative">
          <div className="grid md:grid-cols-12 grid-cols-1 gap-6">
            {/* Filter Section - Mobile */}
            <div
              className={`${
                isFilterOpen ? "block" : "hidden"
              } md:hidden fixed inset-0 z-50 bg-black bg-opacity-50`}
              onClick={() => setIsFilterOpen(false)}
            >
              <div
                className="absolute left-0 top-0 h-full w-4/5 bg-white dark:bg-slate-800 shadow-lg overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h5 className="text-xl font-medium">Filters</h5>
                    <button
                      onClick={() => setIsFilterOpen(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <Filter
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    selectedBrands={selectedBrands}
                    setSelectedBrands={setSelectedBrands}
                    selectedColors={selectedColors}
                    setSelectedColors={setSelectedColors}
                    selectedSizes={selectedSizes}
                    setSelectedSizes={setSelectedSizes}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    onClose={() => setIsFilterOpen(false)}
                  />
                </div>
              </div>
            </div>

            {/* Filter Section - Desktop */}
            <div
              className="hidden md:block md:col-span-3 lg:col-span-3 col-span-1 overflow-y-auto pr-2"
              style={{ maxHeight: "calc(100vh - 200px)" }}
            >
              <Filter
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedColors={selectedColors}
                setSelectedColors={setSelectedColors}
                selectedSizes={selectedSizes}
                setSelectedSizes={setSelectedSizes}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>

            {/* Products Section */}
            <div
              className="lg:col-span-9 md:col-span-9 col-span-1 overflow-y-auto"
              style={{ maxHeight: "calc(100vh - 200px)" }}
            >
              {loading ? (
                <p>Loading products...</p>
              ) : error ? (
                <p className="text-red-500">{error}</p>
              ) : (
                (() => {
                  // ✅ 1️⃣ Decide which product list to show
                  const hasSearch = searchTerm?.trim() !== "";
                  const hasFilters =
                    selectedCategories.length > 0 ||
                    selectedBrands.length > 0 ||
                    selectedColors.length > 0 ||
                    selectedSizes.length > 0;

                  // ✅ 2️⃣ Choose correct product array
                  const displayProducts =
                    hasSearch || hasFilters ? filteredProducts : products;

                  // ✅ 3️⃣ Handle pagination if needed
                  const paginatedProducts = currentProducts?.length
                    ? currentProducts
                    : displayProducts;

                  // ✅ 4️⃣ Conditional rendering
                  if (displayProducts.length === 0) {
                    return <p>No products found.</p>;
                  }

                  return (
                    <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-2 gap-6">
                      {paginatedProducts.map((item, index) => {
                        const firstVariant = item.variants?.[0];
                        const firstSize = firstVariant?.sizes?.[0];
                        const discountedPrice =
                          firstSize?.price?.discounted ||
                          firstSize?.price?.mrp ||
                          null;
                        const originalPrice = firstSize?.price?.mrp || null;
                        const currency = firstSize?.price?.currency || "INR";
                        const currencySymbol = getCurrencySymbol(currency);

                        return (
                          <div className="group" key={index}>
                            <div className="relative overflow-hidden shadow rounded-md duration-500">
                              <img
                                src={
                                  item?.main_image
                                    ? `${BASE_MEDIA_URL}${item.main_image}`
                                    : item?.variants?.[0]?.images?.[0]
                                    ? `${BASE_MEDIA_URL}${item.variants[0].images[0]}`
                                    : "../../../assets/images/categories/football.jpg"
                                }
                                className="w-full h-[260px] object-cover group-hover:scale-110 duration-500"
                                alt={item.name}
                                onError={(e) => {
                                  if (e.target.src !== fallbackImage) {
                                    // Prevent infinite loop
                                    e.target.src = fallbackImage;
                                  }
                                }}
                              />
                              <ul className="list-none absolute top-[10px] end-4 opacity-0 group-hover:opacity-100 duration-500 space-y-1">
                                <li>
                                  <button
                                    onClick={(e) =>
                                      handleAddToWishlist(e, item)
                                    }
                                    className="size-10 inline-flex items-center justify-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                                    title="Add to wishlist"
                                  >
                                    <FiHeart className="size-4" />
                                  </button>
                                </li>
                                <li className="mt-1 ms-0">
                                  <button
                                    onClick={() =>
                                      handleProductClick(item?.product_id)
                                    }
                                    className="size-10 inline-flex items-center justify-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                                  >
                                    <FiEye className="size-4" />
                                  </button>
                                </li>
                                <li className="mt-1 ms-0">
                                  <button
                                    onClick={() => handleCustomizeClick(item)}
                                    className="size-10 inline-flex items-center justify-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                                  >
                                    <FiEdit className="size-4" />
                                  </button>
                                </li>
                              </ul>
                              <ul className="list-none absolute top-[10px] start-4">
                                {item.offer === true && (
                                  <li>
                                    <Link
                                      to="#"
                                      className="bg-orange-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5"
                                    >
                                      {item.tag || "Offer"}
                                    </Link>
                                  </li>
                                )}
                                {item.tag === "New" && (
                                  <li>
                                    <Link
                                      to="#"
                                      className="bg-red-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5"
                                    >
                                      {item.tag}
                                    </Link>
                                  </li>
                                )}
                                {item.tag === "Featured" && (
                                  <li>
                                    <Link
                                      to="#"
                                      className="bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5"
                                    >
                                      {item.tag}
                                    </Link>
                                  </li>
                                )}
                                {discountedPrice &&
                                  originalPrice &&
                                  discountedPrice < originalPrice && (
                                    <li>
                                      <Link
                                        to="#"
                                        className="bg-orange-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5"
                                      >
                                        Sale
                                      </Link>
                                    </li>
                                  )}
                              </ul>
                            </div>

                            <div className="mt-4">
                              <button
                                onClick={() =>
                                  handleProductClick(item?.product_id)
                                }
                                className="text-center hover:text-green-500"
                              >
                                <span className="text-m font-medium mt-1 block">
                                  {item?.brand?.name
                                    ? `${item.brand.name} ${item.name}`
                                    : item?.name}
                                </span>
                              </button>
                              <div className="flex justify-between items-center mt-1">
                                <p className="text-sm sm:text-base">
                                  {discountedPrice !== null
                                    ? `${currencySymbol}${discountedPrice}`
                                    : "Price not available"}
                                  {originalPrice !== null &&
                                    originalPrice !== discountedPrice && (
                                      <del className="text-slate-400">
                                        {currencySymbol}
                                        {originalPrice}
                                      </del>
                                    )}
                                </p>
                                <ul className="flex text-xs sm:text-base text-amber-400 list-none">
                                  {[...Array(5)].map((_, i) => (
                                    <li key={i} className="inline-block">
                                      <i
                                        className={
                                          i < Math.round(item.ratings?.avg || 5)
                                            ? "mdi mdi-star"
                                            : "mdi mdi-star-outline"
                                        }
                                      ></i>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              {item.description && (
                                <p className="text-sm text-slate-500 mt-1">
                                  {item.description.substring(0, 100)}...
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                className={`px-4 py-2 rounded border ${
                  currentPage === index + 1
                    ? "bg-blue-500 text-white"
                    : "bg-white hover:bg-gray-100"
                }`}
              >
                {index + 1}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </section>
      <BottomNav />
      <Footer />
      <Switcher />
      <ScrollToTop />
    </>
  );
}
